import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Image,
  SimpleGrid,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { Loading } from "../../../components/Loading";
import VerificationReviewPanel from "../../../components/verification/VerificationReviewPanel";
import { getVendorDetails } from "../../../actions/inventory/vendor.action";

const vendorStatusMap = {
  active: "Active",
  inactive: "Inactive",
  suspended: "Suspended",
  under_review: "Under Review",
  clarification_requested: "Clarification Requested",
  approved: "Approved",
  rejected: "Rejected",
};

const statusColorMap = {
  active: "green",
  inactive: "gray",
  suspended: "red",
  under_review: "yellow",
  clarification_requested: "orange",
  approved: "green",
  rejected: "red",
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatLabel = (value = "") =>
  String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const deriveReviewStatus = (status) => {
  if (["under_review", "approved", "rejected", "clarification_requested"].includes(status)) {
    return status;
  }
  if (status === "active") return "approved";
  if (status === "inactive" || status === "suspended") return "rejected";
  return "under_review";
};

const getDocumentsStatus = (vendor) => {
  if (!vendor) return "Missing";
  const vendorType = vendor.vendorType || vendor?.kyc?.vendorType;
  if (vendorType === "organisation") {
    const org = vendor?.kyc?.organisation || {};
    const required = [
      org?.pancard?.front,
      org?.aadhaar?.front,
      org?.aadhaar?.back,
      org?.ownerPancard?.front,
      org?.gstCertificate,
    ];
    return required.every((value) => typeof value === "string" && value.trim()) ? "Complete" : "Incomplete";
  }

  const individual = vendor?.kyc?.individual || {};
  const required = [
    individual?.aadhaar?.front,
    individual?.aadhaar?.back,
    individual?.pancard?.front,
  ];
  return required.every((value) => typeof value === "string" && value.trim()) ? "Complete" : "Incomplete";
};

const renderField = (label, value) => (
  <Box>
    <Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={1}>
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="semibold" color="gray.800" wordBreak="break-word">
      {value || "—"}
    </Text>
  </Box>
);

const renderDocumentPreview = (label, imageUrl) => (
  <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={3}>
    <Text fontSize="xs" textTransform="uppercase" color="gray.500">
      {label}
    </Text>
    {imageUrl ? (
      <>
        <Box mt={2} borderRadius="md" overflow="hidden" bg="gray.50">
          <Image src={imageUrl} alt={label} w="100%" h="170px" objectFit="cover" />
        </Box>
        <Button
          as="a"
          href={imageUrl}
          target="_blank"
          rel="noreferrer"
          mt={3}
          size="sm"
          variant="outline"
        >
          Open Full Image
        </Button>
      </>
    ) : (
      <Text mt={2} fontSize="sm" color="gray.500">
        Not uploaded
      </Text>
    )}
  </Box>
);

const VendorApprovalDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const selectedRow = location?.state?.row || null;
  const [vendorDetails, setVendorDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchVendorDetails = async () => {
      try {
        const response = await getVendorDetails(id);
        if (!mounted) return;
        setVendorDetails(response?.data?.data || response?.data || null);
      } catch (error) {
        if (!mounted) return;
        toast({
          title: "Failed to fetch vendor details",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (id && !selectedRow?.reviewOnly) {
      fetchVendorDetails();
    } else {
      setLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [id, selectedRow?.reviewOnly, toast]);

  const resolvedData = useMemo(() => {
    const details = vendorDetails || {};
    const kyc = details?.kyc || {};
    const individual = kyc?.individual || {};
    const organisation = kyc?.organisation || {};
    const vendorType = details?.vendorType || selectedRow?.vendorType || kyc?.vendorType || "—";
    const vendorStatus = details?.vendorStatus || selectedRow?.vendorStatus || "under_review";

    return {
      uniqueVendorCode:
        details?.uniqueVendorCode || selectedRow?.uniqueVendorCode || "—",
      vendorName: details?.vendorName || selectedRow?.vendorName || "—",
      ownerName: selectedRow?.ownerName || details?.vendorName || "—",
      vendorType,
      phoneNumber: details?.phoneNumber || selectedRow?.mobileNumber || "—",
      whatsappNumber: details?.whatsappNumber || selectedRow?.whatsappNumber || "—",
      vendorEmail: details?.vendorEmail || selectedRow?.email || "—",
      vendorRating: details?.vendorRating ?? selectedRow?.vendorRating ?? 0,
      vendorPrioritisation:
        details?.vendorPrioritisation ?? selectedRow?.vendorPrioritisation ?? 0,
      submittedAt: selectedRow?.submittedAt || details?.createdAt || null,
      vendorStatus,
      reviewStatus: deriveReviewStatus(vendorStatus),
      documentsStatus:
        selectedRow?.documentsStatus || getDocumentsStatus(details),
      companyName: details?.companyName || "—",
      organisationType: details?.organisationType || "—",
      companyPanNumber: details?.companyPanNumber || "—",
      ownerAadhaarNumber: details?.ownerAadhaarNumber || "—",
      ownerPanNumber: details?.ownerPanNumber || "—",
      gstin: details?.gstin || "—",
      gstState: details?.gstState || "—",
      orgAddress: details?.orgAddress || {},
      aadhaarNumberIndividual: details?.aadhaarNumberIndividual || "—",
      panNumberIndividual: details?.panNumberIndividual || "—",
      tradeNameIndividual: details?.tradeNameIndividual || "—",
      gstinIndividual: details?.gstinIndividual || "—",
      gstStateIndividual: details?.gstStateIndividual || "—",
      companyPanCardFront:
        details?.companyPanCardFront || organisation?.pancard?.front || "",
      aadhaarCardFront:
        details?.aadhaarCardFront ||
        individual?.aadhaar?.front ||
        organisation?.aadhaar?.front ||
        "",
      aadhaarCardBack:
        details?.aadhaarCardBack ||
        individual?.aadhaar?.back ||
        organisation?.aadhaar?.back ||
        "",
      panCardFront: details?.panCardFront || individual?.pancard?.front || "",
      ownerPanCardFront:
        details?.ownerPanCardFront || organisation?.ownerPancard?.front || "",
      gstCertificate:
        details?.gstCertificate ||
        organisation?.gstCertificate ||
        individual?.gstCertificate ||
        "",
    };
  }, [selectedRow, vendorDetails]);

  if (loading) return <Loading />;

  if (!selectedRow && !vendorDetails) {
    return (
      <Stack p={6} gap={4}>
        <BreadCrumbMaker
          items={[
            { link: "/verification", title: "Verification Center" },
            { link: "/verification/vendor-approvals", title: "Vendor Approvals" },
            { link: "#", title: "Vendor Details", on: true },
          ]}
        />
        <Box bg="white" borderRadius="xl" p={6} shadow="sm">
          <Text color="gray.600">Vendor details not found.</Text>
          <Button mt={4} onClick={() => navigate("/verification/vendor-approvals")}>
            Back to Vendor Approvals
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack p={6} gap={4}>
      <BreadCrumbMaker
        items={[
          { link: "/verification", title: "Verification Center" },
          { link: "/verification/vendor-approvals", title: "Vendor Approvals" },
          { link: "#", title: "Vendor Details", on: true },
        ]}
      />

      <VerificationReviewPanel
        entityType="vendor"
        entityId={selectedRow?.reviewOnly ? selectedRow?.entityId || null : selectedRow?.entityId || id}
      />

      <Box bg="white" borderRadius="xl" p={6} shadow="sm">
        <Flex justify="space-between" align="center" mb={5} gap={3} wrap="wrap">
          <Text fontSize="xl" fontWeight="bold" color="gray.700">
            Vendor Details
          </Text>
          {/* <Button variant="outline" onClick={() => navigate("/verification/vendor-approvals")}>
            Back to List
          </Button> */}
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {renderField("Vendor Code", resolvedData.uniqueVendorCode)}
          {renderField("Vendor Name", resolvedData.vendorName)}
          {renderField("Owner Name", resolvedData.ownerName)}
          {renderField("Vendor Type", formatLabel(resolvedData.vendorType))}
          {renderField("Phone Number", resolvedData.phoneNumber)}
          {renderField("WhatsApp Number", resolvedData.whatsappNumber)}
          {renderField("Email", resolvedData.vendorEmail)}
          {renderField("Rating", resolvedData.vendorRating)}
          {renderField("Prioritisation", resolvedData.vendorPrioritisation)}
          {renderField("Submitted At", formatDate(resolvedData.submittedAt))}
        </SimpleGrid>

        <Divider my={5} />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {renderField("Review Status", formatLabel(resolvedData.reviewStatus))}
          {renderField("Documents", resolvedData.documentsStatus)}
          <Box>
            <Text fontSize="xs" textTransform="uppercase" color="gray.500" mb={1}>
              Current Backend Status
            </Text>
            <Badge colorScheme={statusColorMap[resolvedData.vendorStatus] || "gray"}>
              {vendorStatusMap[resolvedData.vendorStatus] || formatLabel(resolvedData.vendorStatus)}
            </Badge>
          </Box>
        </SimpleGrid>

        {resolvedData.vendorType === "organisation" ? (
          <>
            <Divider my={5} />
            <Text fontSize="sm" fontWeight="semibold" mb={3}>
              Organisation Details
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {renderField("Company Name", resolvedData.companyName)}
              {renderField("Organisation Type", resolvedData.organisationType)}
              {renderField("Company PAN", resolvedData.companyPanNumber)}
              {renderField("Owner Aadhaar", resolvedData.ownerAadhaarNumber)}
              {renderField("Owner PAN", resolvedData.ownerPanNumber)}
              {renderField("GSTIN", resolvedData.gstin)}
              {renderField("GST State", resolvedData.gstState)}
              {renderField("Address", resolvedData.orgAddress?.address)}
              {renderField("City", resolvedData.orgAddress?.city)}
              {renderField("State", resolvedData.orgAddress?.state)}
              {renderField("Pincode", resolvedData.orgAddress?.pinCode)}
              {renderField("Country", resolvedData.orgAddress?.country)}
            </SimpleGrid>
          </>
        ) : (
          <>
            <Divider my={5} />
            <Text fontSize="sm" fontWeight="semibold" mb={3}>
              Individual KYC
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {renderField("Aadhaar Number", resolvedData.aadhaarNumberIndividual)}
              {renderField("PAN Number", resolvedData.panNumberIndividual)}
              {renderField("GST Trade Name", resolvedData.tradeNameIndividual)}
              {renderField("GSTIN", resolvedData.gstinIndividual)}
              {renderField("GST State", resolvedData.gstStateIndividual)}
            </SimpleGrid>
          </>
        )}

        <Divider my={5} />
        <Text fontSize="sm" fontWeight="semibold" mb={3}>
          Uploaded Documents
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {resolvedData.vendorType === "organisation" ? (
            <>
              {renderDocumentPreview("Company PAN Card", resolvedData.companyPanCardFront)}
              {renderDocumentPreview("Owner Aadhaar Front", resolvedData.aadhaarCardFront)}
              {renderDocumentPreview("Owner Aadhaar Back", resolvedData.aadhaarCardBack)}
              {renderDocumentPreview("Owner PAN Card", resolvedData.ownerPanCardFront)}
              {renderDocumentPreview("GST Certificate", resolvedData.gstCertificate)}
            </>
          ) : (
            <>
              {renderDocumentPreview("Aadhaar Card Front", resolvedData.aadhaarCardFront)}
              {renderDocumentPreview("Aadhaar Card Back", resolvedData.aadhaarCardBack)}
              {renderDocumentPreview("PAN Card Front", resolvedData.panCardFront)}
              {renderDocumentPreview("GST Certificate", resolvedData.gstCertificate)}
            </>
          )}
        </SimpleGrid>
      </Box>
    </Stack>
  );
};

export default VendorApprovalDetails;
